import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { getAuthToken } from 'lib/auth'
import copyToClipboard from '../../../utils/copyToClipboard'
import { Message, StreamingResponse } from './types'
import {
  useGetMessagesFromBotQuery,
  useCreateFlowChatSessionMutation,
  OnboardingFlowType,
} from '../../../__generated__/api-types-and-hooks'

interface InitialMessage {
  content: string
  role: 'assistant' | 'user'
}

// Storage key for pending requests
const PENDING_REQUESTS_KEY = 'insight-feedback-pending-requests'
const RESPONSE_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes
const POLL_INTERVAL_MS = 3000 // 3 seconds

interface PendingRequest {
  sessionId: string
  userMessage: string
  timestamp: number
}

// In-memory cache for chat state (persists across dialog open/close)
interface ChatStateCache {
  messages: Message[]
  sessionId: string | null
  isTyping: boolean
  lastAccessed: number // For LRU cleanup
}
const chatStateCache: Record<string, ChatStateCache> = {}
const MAX_CACHE_ENTRIES = 20 // Limit cache size to prevent memory leaks

/**
 * Cleanup old cache entries if cache exceeds max size (LRU eviction)
 */
function cleanupOldCacheEntries() {
  const keys = Object.keys(chatStateCache)
  if (keys.length <= MAX_CACHE_ENTRIES) return

  // Sort by lastAccessed (oldest first) and remove excess entries
  const sortedKeys = keys.sort(
    (a, b) => (chatStateCache[a].lastAccessed || 0) - (chatStateCache[b].lastAccessed || 0)
  )
  const entriesToRemove = sortedKeys.slice(0, keys.length - MAX_CACHE_ENTRIES)
  entriesToRemove.forEach((key) => delete chatStateCache[key])
  console.log(`Cache cleanup: removed ${entriesToRemove.length} old entries`)
}

/**
 * Get pending requests from localStorage
 */
function getPendingRequests(): Record<string, PendingRequest> {
  try {
    const stored = localStorage.getItem(PENDING_REQUESTS_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

/**
 * Save pending request to localStorage
 */
function savePendingRequest(sessionId: string, userMessage: string) {
  try {
    const pending = getPendingRequests()
    pending[sessionId] = { sessionId, userMessage, timestamp: Date.now() }
    localStorage.setItem(PENDING_REQUESTS_KEY, JSON.stringify(pending))
  } catch (e) {
    console.error('Failed to save pending request:', e)
  }
}

/**
 * Remove pending request from localStorage
 */
function removePendingRequest(sessionId: string) {
  try {
    const pending = getPendingRequests()
    delete pending[sessionId]
    localStorage.setItem(PENDING_REQUESTS_KEY, JSON.stringify(pending))
  } catch (e) {
    console.error('Failed to remove pending request:', e)
  }
}

/**
 * Parse timestamp from various formats (unix ms, unix seconds, ISO string)
 */
function parseTimestamp(timestamp: string | number): Date {
  if (typeof timestamp === 'number') {
    return timestamp > 10000000000 ? new Date(timestamp) : new Date(timestamp * 1000)
  }
  const numericTs = parseInt(timestamp, 10)
  if (!isNaN(numericTs)) {
    return numericTs > 10000000000 ? new Date(numericTs) : new Date(numericTs * 1000)
  }
  const date = new Date(timestamp)
  return isNaN(date.getTime()) ? new Date() : date
}

/**
 * Check if a message timestamp is recent (within timeout period)
 */
function isRecentMessage(timestamp: Date): boolean {
  return Date.now() - timestamp.getTime() < RESPONSE_TIMEOUT_MS
}

/**
 * Hook for managing Coach Logic streaming chat with backend persistence
 */
export function useCoachLogicChat(insightId?: string, initialSessionId?: string | null) {
  // Get cache key based on insightId
  const cacheKey = insightId || 'default'

  // Direct cache read function - always reads current cache
  const getCache = () => chatStateCache[cacheKey]
  const cachedState = getCache() // Read once per render

  // Initialize state from cache or defaults
  const [messages, setMessagesState] = useState<Message[]>(cachedState?.messages || [])
  const [sessionId, setSessionIdState] = useState<string | null>(cachedState?.sessionId || null)
  const [isTyping, setIsTypingState] = useState(cachedState?.isTyping || false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [shouldFetchHistory, setShouldFetchHistory] = useState(false)
  const [requestFailed, setRequestFailed] = useState(false)

  // Keep a ref to current values for synchronous cache updates and access in callbacks
  const messagesRef = useRef<Message[]>(cachedState?.messages || [])
  const sessionIdRef = useRef<string | null>(cachedState?.sessionId || null)
  const isTypingRef = useRef<boolean>(cachedState?.isTyping || false)

  // Track the previous cacheKey to detect changes
  const prevCacheKeyRef = useRef<string>(cacheKey)

  // Use useLayoutEffect for synchronous state syncing when cacheKey changes
  // This handles the rare case where insightId changes without unmounting
  // (e.g., if the dialog is used for multiple insights in succession)
  useLayoutEffect(() => {
    // Skip on initial mount - useState initializers already have correct values
    if (prevCacheKeyRef.current !== cacheKey) {
      prevCacheKeyRef.current = cacheKey

      const cached = chatStateCache[cacheKey]

      // Update refs first (synchronous access for callbacks)
      messagesRef.current = cached?.messages || []
      sessionIdRef.current = cached?.sessionId || null
      isTypingRef.current = cached?.isTyping || false

      // Force state update to match cache
      setMessagesState(cached?.messages || [])
      setSessionIdState(cached?.sessionId || null)
      setIsTypingState(cached?.isTyping || false)
      setIsLoadingHistory(false)
      setShouldFetchHistory(false)
    }
  }, [cacheKey])

  // Wrapper functions that update cache synchronously
  const setMessages = useCallback(
    (updater: Message[] | ((prev: Message[]) => Message[])) => {
      const newMessages = typeof updater === 'function' ? updater(messagesRef.current) : updater
      // Update ref and cache synchronously
      messagesRef.current = newMessages
      chatStateCache[cacheKey] = {
        messages: newMessages,
        sessionId: sessionIdRef.current,
        isTyping: isTypingRef.current,
        lastAccessed: Date.now(),
      }
      // Cleanup old cache entries to prevent memory leaks
      cleanupOldCacheEntries()
      // Then update React state
      setMessagesState(newMessages)
    },
    [cacheKey]
  )

  const setSessionId = useCallback(
    (id: string | null) => {
      sessionIdRef.current = id
      chatStateCache[cacheKey] = {
        messages: messagesRef.current,
        sessionId: id,
        isTyping: isTypingRef.current,
        lastAccessed: Date.now(),
      }
      setSessionIdState(id)
    },
    [cacheKey]
  )

  const setIsTyping = useCallback(
    (typing: boolean) => {
      isTypingRef.current = typing
      chatStateCache[cacheKey] = {
        messages: messagesRef.current,
        sessionId: sessionIdRef.current,
        isTyping: typing,
        lastAccessed: Date.now(),
      }
      setIsTypingState(typing)
    },
    [cacheKey]
  )

  // Track initialization
  const initializedRef = useRef<string>('')

  // Track active streaming request for cleanup
  const abortControllerRef = useRef<AbortController | null>(null)

  // Track polling interval for cleanup
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pollingErrorCountRef = useRef<number>(0) // Track consecutive polling errors
  const MAX_POLLING_ERRORS = 3 // Stop polling after this many consecutive errors

  // Mutation to create a new session
  const { mutateAsync: createFlowChatSession } = useCreateFlowChatSessionMutation()

  // Fetch messages from backend
  const {
    data: messagesData,
    isLoading: isQueryLoading,
    refetch,
  } = useGetMessagesFromBotQuery(
    { sessionId: sessionId || '', limit: 100 },
    {
      enabled: !!sessionId && shouldFetchHistory,
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5000,
    }
  )

  // Cleanup polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }
    pollingErrorCountRef.current = 0 // Reset error count when stopping
  }, [])

  // Convert backend messages to local format
  const convertMessages = useCallback((backendMsgs: any[]): Message[] => {
    const converted = backendMsgs.map((msg, index) => ({
      id: `msg-${index}-${msg.createdAt}`,
      content: msg.content,
      sender: (msg.role === 'user' ? 'user' : 'coach') as 'user' | 'coach',
      timestamp: parseTimestamp(msg.createdAt),
      isVoiceMessage: msg.isVoiceMessage || false,
      voiceDuration: msg.voiceDuration || undefined,
    }))
    // Sort by timestamp ascending (oldest first)
    converted.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
    return converted
  }, [])

  // Start polling for response
  const startPolling = useCallback(
    (sid: string) => {
      // Clear any existing polling
      stopPolling()

      console.log('Starting polling for session:', sid)

      pollingIntervalRef.current = setInterval(async () => {
        try {
          const result = await refetch()
          const msgs = result.data?.getMessagesFromBot?.messages || []

          // Reset error count on successful fetch
          pollingErrorCountRef.current = 0

          if (msgs.length > 0) {
            const converted = convertMessages(msgs)
            setMessages(converted)

            const lastMsg = converted[converted.length - 1]
            // If last message is from coach, response is complete
            if (lastMsg.sender === 'coach') {
              console.log('Response received, stopping polling')
              removePendingRequest(sid)
              stopPolling()
              setIsTyping(false)
            }
          }
        } catch (error) {
          pollingErrorCountRef.current += 1
          console.error(
            `Polling error (${pollingErrorCountRef.current}/${MAX_POLLING_ERRORS}):`,
            error
          )

          // Stop polling after too many consecutive errors
          if (pollingErrorCountRef.current >= MAX_POLLING_ERRORS) {
            console.error('Too many polling errors, stopping polling')
            stopPolling()
            removePendingRequest(sid)
            setIsTyping(false)
            setRequestFailed(true)
            setMessages((prev) => [
              ...prev,
              {
                id: `error-network-${Date.now()}`,
                content: 'Unable to get response due to network issues. Please try again.',
                sender: 'coach',
                timestamp: new Date(),
              },
            ])
          }
        }
      }, POLL_INTERVAL_MS)

      // Set timeout to stop polling after 5 minutes
      pollingTimeoutRef.current = setTimeout(() => {
        console.log('Polling timeout reached')
        stopPolling()
        removePendingRequest(sid)
        setIsTyping(false)
        setRequestFailed(true)
        // Add error message
        setMessages((prev) => [
          ...prev,
          {
            id: `error-timeout-${Date.now()}`,
            content: 'The response is taking longer than expected. Please try again.',
            sender: 'coach',
            timestamp: new Date(),
          },
        ])
      }, RESPONSE_TIMEOUT_MS)
    },
    [refetch, convertMessages, stopPolling, setMessages, setIsTyping]
  )

  // Process query results
  useEffect(() => {
    if (!sessionId || !shouldFetchHistory) return

    if (messagesData?.getMessagesFromBot?.messages !== undefined) {
      const backendMsgs = messagesData.getMessagesFromBot.messages || []
      if (backendMsgs.length > 0) {
        const converted = convertMessages(backendMsgs)
        setMessages(converted)

        // Check if last message is from user and is recent - means response is pending
        const lastMsg = converted[converted.length - 1]
        if (lastMsg?.sender === 'user' && isRecentMessage(lastMsg.timestamp)) {
          console.log('Last message is from user and recent, showing loader and starting polling')
          setIsTyping(true)
          startPolling(sessionId)
        } else {
          // Response already received or message is old
          removePendingRequest(sessionId)
          setIsTyping(false)
        }
      } else {
        // Empty messages from backend - stream might have failed
        // Clear messages so FeedbackDialog can show greeting messages
        console.log('No messages from backend, clearing to show greeting messages')
        setMessages([])
        setIsTyping(false)
      }
      setShouldFetchHistory(false)
      setIsLoadingHistory(false)
    } else if (!isQueryLoading && messagesData) {
      setShouldFetchHistory(false)
      setIsLoadingHistory(false)
    }
  }, [
    messagesData,
    isQueryLoading,
    sessionId,
    shouldFetchHistory,
    convertMessages,
    startPolling,
    setMessages,
    setIsTyping,
  ])

  // Sync loading state
  useEffect(() => {
    if (shouldFetchHistory && isQueryLoading) {
      setIsLoadingHistory(true)
    }
  }, [isQueryLoading, shouldFetchHistory])

  // Cleanup on unmount - only stop polling, DON'T abort the request
  // The streaming request continues in background and updates the cache
  // When user reopens, they'll see the response from cache
  useEffect(() => {
    return () => {
      // NOTE: We intentionally do NOT abort the request here
      // This allows the streaming to continue and update the cache
      // even when the dialog is closed
      stopPolling()
    }
  }, [stopPolling])

  // Send message to Coach Logic streaming API (non-blocking)
  const sendMessage = useCallback(
    async (
      userMessage: string,
      currentSessionId: string | null,
      onSessionCreated?: (sessionId: string) => void,
      initialMessages?: InitialMessage[],
      systemPrompt?: string // Optional system prompt for context-aware conversations
    ) => {
      // Set typing immediately for optimistic UI
      setIsTyping(true)
      setRequestFailed(false)

      // Abort any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller and store signal reference
      const abortController = new AbortController()
      abortControllerRef.current = abortController
      const { signal } = abortController

      let activeSessionId = currentSessionId

      try {
        const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
        const authToken = await getAuthToken()

        if (!authToken) {
          throw new Error('Authentication token is required')
        }

        // Check if aborted before continuing
        if (signal.aborted) {
          return activeSessionId
        }

        // If no session exists, create a new one first via GraphQL mutation
        if (!activeSessionId) {
          const response = await createFlowChatSession({
            input: {
              flow: OnboardingFlowType.GeneralChat,
            },
          })
          activeSessionId = response.createFlowChatSession.id
          setSessionId(activeSessionId)
          onSessionCreated?.(activeSessionId)
        }

        // Check if aborted before continuing
        if (signal.aborted) {
          return activeSessionId
        }

        // Save pending request for recovery if user navigates away
        savePendingRequest(activeSessionId, userMessage)

        const requestBody: Record<string, unknown> = {
          userMessage,
          sessionId: activeSessionId,
        }

        // Include initial messages if provided (for first message in new session)
        if (initialMessages && initialMessages.length > 0) {
          requestBody.initialMessages = initialMessages
        }

        // Include system prompt if provided (for context-aware feedback conversations)
        if (systemPrompt) {
          requestBody.systemPrompt = systemPrompt
        }

        const response = await fetch(streamChatbotUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify(requestBody),
          signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body reader available')
        }

        const botMessageId = `bot-${Date.now()}`
        const decoder = new TextDecoder()
        let buffer = ''
        let fullContent = ''
        let messageAdded = false

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.trim()) {
              try {
                const data: StreamingResponse = JSON.parse(line)

                if (data.type === 'session' || data.type === 'status') {
                  continue
                } else if (data.type === 'content') {
                  fullContent += data.content || ''
                  const currentContent = fullContent
                  if (!messageAdded && currentContent.trim()) {
                    const botMessage: Message = {
                      id: botMessageId,
                      sender: 'coach',
                      content: currentContent,
                      timestamp: new Date(),
                    }
                    setMessages((prev) => [...prev, botMessage])
                    messageAdded = true
                  } else if (messageAdded) {
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessageId ? { ...msg, content: currentContent } : msg
                      )
                    )
                  }
                } else if (data.type === 'complete') {
                  const finalContent = data.fullResponse || fullContent
                  if (!messageAdded && finalContent.trim()) {
                    const botMessage: Message = {
                      id: botMessageId,
                      sender: 'coach',
                      content: finalContent,
                      timestamp: new Date(),
                    }
                    setMessages((prev) => [...prev, botMessage])
                  } else if (messageAdded) {
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessageId ? { ...msg, content: finalContent } : msg
                      )
                    )
                  }
                  removePendingRequest(activeSessionId!)
                } else if (data.type === 'error') {
                  const errorContent =
                    data.error || 'Sorry, I encountered an error. Please try again.'
                  if (!messageAdded) {
                    setMessages((prev) => [
                      ...prev,
                      {
                        id: botMessageId,
                        sender: 'coach',
                        content: errorContent,
                        timestamp: new Date(),
                      },
                    ])
                  }
                  removePendingRequest(activeSessionId!)
                  setRequestFailed(true)
                }
              } catch (e) {
                console.error('Error parsing streaming response:', e)
              }
            }
          }
        }

        removePendingRequest(activeSessionId!)
        setIsTyping(false)
        // Only clear ref if this is still the current controller
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
        return activeSessionId
      } catch (error: any) {
        // Don't show error if request was aborted (user navigated away)
        if (error.name === 'AbortError') {
          console.log('Request aborted - user navigated away')
          // Keep pending request and typing state so polling can pick it up when user returns
          return activeSessionId
        }

        console.error('Error sending message to Coach Logic:', error)
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            sender: 'coach',
            content: 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date(),
          },
        ])

        if (activeSessionId) {
          removePendingRequest(activeSessionId)
        }
        setRequestFailed(true)
        setIsTyping(false)
        // Only clear ref if this is still the current controller
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
        return currentSessionId
      }
    },
    [createFlowChatSession, setIsTyping, setMessages, setSessionId]
  )

  // Add a user message to local state
  const addUserMessage = useCallback(
    (content: string, options?: Partial<Message>) => {
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        content,
        sender: 'user',
        timestamp: new Date(),
        ...options,
      }
      setMessages((prev) => [...prev, userMessage])
      return userMessage
    },
    [setMessages]
  )

  // Initialize - set session and trigger loading if needed
  const initialize = useCallback(
    (existingSession?: string | null) => {
      const initKey = `${insightId}-${existingSession || 'new'}`

      // Check if we have cached state FIRST (user closed dialog while waiting for response)
      const cached = chatStateCache[cacheKey]
      if (cached?.messages?.length > 0) {
        // Check if last message is from coach - if so, response is complete, isTyping should be false
        const lastMessage = cached.messages[cached.messages.length - 1]
        const isResponseComplete = lastMessage?.sender === 'coach'
        const shouldBeTyping = cached.isTyping && !isResponseComplete

        // Always restore from cache if available - update refs and state
        messagesRef.current = cached.messages
        sessionIdRef.current = cached.sessionId
        isTypingRef.current = shouldBeTyping
        setMessagesState(cached.messages)
        setSessionIdState(cached.sessionId)
        setIsTypingState(shouldBeTyping)
        setShouldFetchHistory(false)
        setIsLoadingHistory(false)
        setRequestFailed(false)
        initializedRef.current = initKey

        // Update cache with corrected isTyping state
        if (cached.isTyping !== shouldBeTyping) {
          chatStateCache[cacheKey] = {
            ...cached,
            isTyping: shouldBeTyping,
            lastAccessed: Date.now(),
          }
        }

        // If still typing, start polling to get the response
        if (shouldBeTyping && cached.sessionId) {
          startPolling(cached.sessionId)
        }

        return { hasExisting: true }
      }

      // Prevent re-initialization with same values (only if no cache)
      if (initializedRef.current === initKey) {
        return { hasExisting: !!existingSession }
      }

      initializedRef.current = initKey
      setRequestFailed(false)

      if (existingSession) {
        setSessionId(existingSession)
        setShouldFetchHistory(true)
        setIsLoadingHistory(true)
        return { hasExisting: true }
      }

      // No existing session - start fresh
      setMessages([])
      setSessionId(null)
      setShouldFetchHistory(false)
      setIsLoadingHistory(false)
      return { hasExisting: false }
    },
    [insightId, cacheKey, startPolling, setMessages, setSessionId]
  )

  // Reset the conversation
  const reset = useCallback(() => {
    setMessages([])
    setSessionId(null)
    setIsTyping(false)
    setIsLoadingHistory(false)
    setShouldFetchHistory(false)
    setRequestFailed(false)
    initializedRef.current = ''
    stopPolling()
    // Clear cache for this insight
    delete chatStateCache[cacheKey]
  }, [stopPolling, setMessages, setSessionId, setIsTyping, cacheKey])

  // Check if user has sent a message
  const hasUserMessage = messages.some((m) => m.sender === 'user')

  return {
    messages,
    setMessages,
    sessionId,
    setSessionId,
    isTyping,
    isLoadingHistory,
    requestFailed,
    sendMessage,
    addUserMessage,
    initialize,
    reset,
    hasUserMessage,
  }
}

/**
 * Hook for text-to-speech functionality
 */
export function useSpeechSynthesis() {
  const [isSpeaking, setIsSpeaking] = useState(false)
  const isMountedRef = useRef(true)

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel()
      }
    }
  }, [])

  const speak = useCallback(
    (text: string) => {
      try {
        if (!('speechSynthesis' in window)) {
          console.warn('Speech synthesis not supported')
          return
        }

        if (isSpeaking) {
          speechSynthesis.cancel()
          if (isMountedRef.current) setIsSpeaking(false)
          return
        }

        speechSynthesis.cancel()

        const speakText = () => {
          const utterance = new SpeechSynthesisUtterance(text)
          utterance.rate = 0.9
          utterance.pitch = 1.0
          utterance.volume = 1.0

          const voices = speechSynthesis.getVoices()
          const preferredVoice =
            voices.find(
              (voice) =>
                voice.lang.startsWith('en') &&
                (voice.name.includes('Female') || voice.name.includes('Google'))
            ) ||
            voices.find((voice) => voice.lang.startsWith('en')) ||
            voices[0]

          if (preferredVoice) utterance.voice = preferredVoice

          utterance.onstart = () => {
            if (isMountedRef.current) setIsSpeaking(true)
          }
          utterance.onend = () => {
            if (isMountedRef.current) setIsSpeaking(false)
          }
          utterance.onerror = () => {
            if (isMountedRef.current) setIsSpeaking(false)
          }

          speechSynthesis.speak(utterance)
        }

        const voices = speechSynthesis.getVoices()
        if (voices.length === 0) {
          speechSynthesis.addEventListener('voiceschanged', speakText, { once: true })
        } else {
          speakText()
        }
      } catch (error) {
        console.error('Failed to speak:', error)
        if (isMountedRef.current) setIsSpeaking(false)
      }
    },
    [isSpeaking]
  )

  const cancel = useCallback(() => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel()
    }
    if (isMountedRef.current) setIsSpeaking(false)
  }, [])

  return { isSpeaking, speak, cancel }
}

/**
 * Hook for clipboard operations
 */
export function useClipboard() {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const copy = useCallback(async (text: string, id: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        copyToClipboard(text)
        setCopiedId(id)
        setTimeout(() => setCopiedId(null), 2000)
        return
      }

      // Fallback for older browsers / non-secure context
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      textArea.setAttribute('readonly', '')
      document.body.appendChild(textArea)

      textArea.select()
      textArea.setSelectionRange(0, 99999)

      document.execCommand('copy')
      textArea.remove()

      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      console.error('Failed to copy text:', error)
      alert('Failed to copy to clipboard. Please select the text and copy manually.')
    }
  }, [])

  return { copiedId, copy }
}
